import React from 'react';
import { cloneDeep, get, head, isEmpty, isEqual, isNil, set, find, findIndex } from 'lodash';
import queryString from 'query-string';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS } from 'constants/common';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as errorOverlay from 'utils/errorOverlay';

import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';

// React view component to render
import { callWidgetItemDetailSummary as CallWidgetItemDetailSummaryView } from 'ui/views/widgets/callWidgetItemDetailSummary';
import { CallWidgetItemDetailSummaryService } from 'services/callWidgetItemDetailSummary';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallWidgetItemDetailSummary } from 'ui/containers/CallWidgetItemDetailSummary';
 *
 * // render
 * <CallWidgetItemDetailSummary propName='propValue'/>
 */
export class CallWidgetItemDetailSummary extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
         // component state
         this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // bind methods
        this.handleActionClick = this.handleActionClick.bind( this );

        // create service instance
        this.callWidgetItemDetailSummaryService = new CallWidgetItemDetailSummaryService();
        
        // start data fetch
        this.fetchComponentData();
    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CallWidgetItemDetailSummary.fetchComponentData()' );

        // cancel ongoing request
        this.callWidgetItemDetailSummaryService.cancel();

        // fetch component data
        const { status, data } = await this.callWidgetItemDetailSummaryService.fetchComponentData(  {
            url: this.props.widget.url,
            data: { ...this.props.requestPayload }
        } );

        // handles dynamic columns
        const updatedData = this.handleDynamicColumns( { data } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: updatedData,
            } );
        }
    }

    async callMarkResolved( { url, callData, config } ) {
        log.debug( 'CallWidgetItemDetailSummary.callMarkResolved()' );
        
        // cancel ongoing request
        this.callWidgetItemDetailSummaryService.cancel();

        pageLevelPreloader.show();

        // fetch component data
        const { status, data } = await this.callWidgetItemDetailSummaryService.fetchMarkResolvedData(  {
            url: url,
            data: {}
        } );

        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CALL_MARK_RESOLVED,
                    showCloseButton: false,
                    ignoreOutsideClick: false
                },
                config: {},
                data: {
                    ...data,
                    onSumbitClick: async ( { values } ) => {
                        
                        overlay.close();

                           // fetch component data
                        const { _status } = await this.callWidgetItemDetailSummaryService.markResolvedData( {
                            url: config.url,
                            data: {
                                context: {},
                                data: {
                                    ...callData,
                                    ...values
                                }
                            }
                        } );

                        // cancel ongoing request
                        this.callWidgetItemDetailSummaryService.cancel();

                        if( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            const cloneList = cloneDeep( this.state.componentData );
                            const callDetail = head( cloneList.data.state.items );
                            if( ! isEmpty( callDetail ) ) {
                                
                                // when success, call mark resolved action will be hiden and resolution will mark as resolved
                                if( isEqual( callDetail.id, callData.callId ) ) {
                                    set( callDetail, 'actionsVisibility.groupActions.markResolved', false );
                                    set( callDetail, 'values.resolution', 'resolved' );
                                }
                                
                                set( cloneList, 'data.state.items', [ callDetail ] );

                                this.setState( {
                                    componentData: cloneList
                                } );
                            }

                            successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ), } );

                        } else {
                            errorOverlay.open();
                        }

                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );

        }
    }

     /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {} ) {
        log.debug( 'CallWidgetItemDetailSummary.handleActionClick()', type, value, item ); // @TODO: remove

        if( 'column' === type ) {
            this.props.history.push( { pathname: DASHBOARD_SUB_ROUTES.CALL_DETAIL, search: queryString.stringify( {
                callId: item.values.callId,
            }  ) } );
        }

        if( 'groupAction' === type ) {

            this.callMarkResolved( {
                url: value.url,
                callData: {
                    callId: item.id
                },
                config: value.config
            } );
        }
    }

    /**
    * @desc Handler for handling dynamic columns.
    */
   handleDynamicColumns( { data } ) {
    log.debug( 'CallWidgetItemDetailSummary.handleDynamicColumns()' );

    const _data = cloneDeep( data );

    // gets values
    const values = get( head( get( _data, 'data.state.items', [] ) ), 'values', {} );

    // gets callerId details
    const callerId = get( values, 'callerId', {} );

    if ( !isEmpty( callerId ) && !isNil( callerId ) ) {

        const key = get( callerId, 'name', '' );
        const value = get( callerId, 'value', '' );

        if ( ( !isEmpty( key ) && !isNil( key ) ) && ( !isEmpty( value ) && !isNil( value ) ) ) {

            const primaryColumns = get( _data, 'structure.primaryColumns', [] );
            const dynamicColumns = get( _data, 'structure.dynamicColumns', {} );
            const placementAfter = get( dynamicColumns, 'placementAfter', 'callCallerType' );

            // finds dynamic column
            const callerIdColumn = find( get( dynamicColumns, 'columns', [] ), { name: key } );

            // finds index of field after which dynamic column needs to be added
            const indexOfEarlierField = findIndex( primaryColumns, { name: placementAfter } );

            // inserts dynamic column after index found above
            // mutated array on purpose
            primaryColumns.splice( indexOfEarlierField + 1, 0, callerIdColumn );

            // updates values to be used by dynamic column
            // mutated object on purpose
            values[ key ] = value;

            // returned updated data
            // with mutated values
            return _data;
        }
    }

    // return data by default
    return data;

   }


    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <CallWidgetItemDetailSummaryView
            isFetching={ this.state.isFetching }
            networkStatus={ this.state.networkStatus }
            componentData={ get( this.state, 'componentData', {} ) }
            onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
CallWidgetItemDetailSummary.displayName = 'CallWidgetItemDetailSummary';

// set default props
CallWidgetItemDetailSummary.defaultProps = {};

// prop types
CallWidgetItemDetailSummary.propTypes = {};
